import { SxProps, TextField, Typography, useTheme } from '@mui/material';
import React, { useState, useCallback, useImperativeHandle } from 'react';
import { ITheme } from 'types/theme';
import { CodeEditorView } from './CodeEditorView';
import { EditorManager, ICodeEditorViewProps, Lang } from './types';

interface ICodeEditInputProps
	extends Omit<ICodeEditorViewProps, 'currentTheme'> {
	label: string;
	lang: Lang;
}

const containerStyle: SxProps = {
	flexGrow: 1,
	height: '350px',
	minHeight: '350px',
	maxHeight: '800px',
	overflow: 'scroll',
	resize: 'vertical'
};

const CodeEditorWrapper = (props: ICodeEditorViewProps, ref: any) => {
	const {
		id,
		editable,
		values,
		currentTheme,
		onChange,
		lang,
		containerSx,
		...rest
	} = props;
	return (
		<Typography
			fontSize="medium"
			pt={1}
			component="div"
			sx={{ ...containerStyle, ...containerSx }}
			ref={ref}
		>
			<CodeEditorView
				id={id}
				editable={editable}
				onChange={onChange}
				values={values}
				currentTheme={currentTheme}
				lang={lang}
				{...rest}
			/>
		</Typography>
	);
};

const CustomInputWrapper = React.forwardRef<any, ICodeEditorViewProps>(
	CodeEditorWrapper
);

export const CodeEditorInput = React.forwardRef<
	EditorManager,
	ICodeEditInputProps
>(({ id, label, editable, onChange, values, lang, containerSx }, ref) => {
	const { palette } = useTheme<ITheme>();
	const floatingLabelFocusStyle = {
		background: palette.wizard.card.main,
		borderRadius: '4px'
	};

	const [view, setView] = useState<EditorManager>(undefined);
	const refCallback = useCallback((v: EditorManager) => {
		setView(v);
	}, []);

	useImperativeHandle(ref, () => view, [view]);
	return (
		<TextField
			label={label}
			value={label}
			disabled={!editable}
			onChange={onChange as any} // TextField's doesn't track the input via event, rather passes values to the custom onChange function
			InputProps={{
				inputComponent: CustomInputWrapper as any,
				inputProps: {
					id,
					editable,
					values,
					currentTheme: palette.mode,
					lang,
					containerSx,
					refCallback
				}
			}}
			InputLabelProps={{
				style: floatingLabelFocusStyle
			}}
			variant="outlined"
			sx={{ display: 'flex' }} // For CM to play nice with MUI's `inline-flex` grid
		/>
	);
});

export default CodeEditorInput;
